"use client";
import { ReactNode, useState, useEffect } from "react";
import { FlagMap, FeatureFlagResolverProps } from "./types";
import { evaluateFeatureFlag } from "./evaluateFeatureFlag";

export function FeatureFlagComponentResolverClient<K extends keyof FlagMap>({
  flagKey,
  fallback,
  variants,
}: FeatureFlagResolverProps<K>): ReactNode {
  const [flagValue, setFlagValue] = useState<FlagMap[K] | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFeatureFlag = async () => {
      try {
        const value = (await evaluateFeatureFlag(
          flagKey,
          fallback || "",
        )) as FlagMap[K];
        setFlagValue(value);
      } catch (error) {
        console.error("Error fetching feature flag:", error);
        // If there's an error, use the fallback if provided
        if (fallback) {
          setFlagValue(fallback);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeatureFlag();
  }, [flagKey, fallback]);

  // Show nothing while loading to prevent flicker
  if (isLoading) {
    return null;
  }

  // If the flag value exists and there is a corresponding variant, render it
  if (flagValue && variants[flagValue]) {
    return variants[flagValue];
  }

  // If there is a fallback value and there is a corresponding variant, render it
  if (fallback && variants[fallback]) {
    return variants[fallback];
  }

  return null;
}
